import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  combineHm24,
  formatIncidentTimeTriggerLabel,
  INCIDENT_HOURS_24,
  INCIDENT_QUARTER_MINUTES,
  splitHm24,
} from '../claims/incidentQuarterHourTime'
import { CLAIM_FORM_SELECT_CLASS } from '../claims/claimFormFieldClasses'
import { PrimaryButton } from './ui'

const WHEEL_ITEM_HEIGHT_PX = 44
const WHEEL_VISIBLE_ROWS = 5
const WHEEL_PAD_ROWS = Math.floor(WHEEL_VISIBLE_ROWS / 2)

export interface IncidentQuarterHourWheelPickerProps {
  value: string
  onChange: (next: string) => void
  triggerId: string
  hourLabelId: string
  minuteLabelId: string
  describedBy?: string
  invalid?: boolean
}

function padHourToken(h: string): string {
  if (h === '') return ''
  const n = Number(h)
  if (n < 0 || n > 23) return ''
  return String(n).padStart(2, '0')
}

function scrollColumnToIndex(container: HTMLElement, index: number) {
  container.scrollTop = index * WHEEL_ITEM_HEIGHT_PX
}

function readColumnIndex(container: HTMLElement): number {
  return Math.round(container.scrollTop / WHEEL_ITEM_HEIGHT_PX)
}

interface WheelColumnProps {
  label: string
  labelId: string
  items: readonly string[]
  value: string
  onValueChange: (next: string) => void
}

function WheelColumn({ label, labelId, items, value, onValueChange }: WheelColumnProps) {
  const scrollerRef = useRef<HTMLDivElement>(null)
  const selectedIndex = Math.max(0, items.indexOf(value))

  const syncSelectionFromScroll = useCallback(() => {
    const el = scrollerRef.current
    if (!el) return
    const idx = Math.max(0, Math.min(items.length - 1, readColumnIndex(el)))
    const next = items[idx]
    if (next !== value) onValueChange(next)
  }, [items, onValueChange, value])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    scrollColumnToIndex(el, selectedIndex >= 0 ? selectedIndex : 0)
  }, [selectedIndex])

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    let scrollEndTimer: ReturnType<typeof setTimeout> | undefined
    const onScroll = () => {
      if (scrollEndTimer) clearTimeout(scrollEndTimer)
      scrollEndTimer = setTimeout(syncSelectionFromScroll, 100)
    }
    const onScrollEnd = () => syncSelectionFromScroll()
    el.addEventListener('scroll', onScroll, { passive: true })
    el.addEventListener('scrollend', onScrollEnd)
    return () => {
      el.removeEventListener('scroll', onScroll)
      el.removeEventListener('scrollend', onScrollEnd)
      if (scrollEndTimer) clearTimeout(scrollEndTimer)
    }
  }, [syncSelectionFromScroll])

  return (
    <div className="relative min-w-0 flex-1" role="group" aria-labelledby={labelId}>
      <p id={labelId} className="mb-[8px] text-center font-body text-sm text-text-tertiary-600">
        {label}
      </p>
      <div className="relative">
        <div
          className="pointer-events-none absolute inset-x-[4px] top-1/2 z-10 h-[44px] -translate-y-1/2 rounded-lg border border-border-secondary bg-background-active/40"
          aria-hidden
        />
        <div
          ref={scrollerRef}
          className="h-[220px] overflow-y-auto overscroll-contain scroll-smooth [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
          style={{ scrollSnapType: 'y mandatory' }}
        >
          {Array.from({ length: WHEEL_PAD_ROWS }, (_, i) => (
            <div key={`pad-top-${i}`} className="shrink-0" style={{ height: WHEEL_ITEM_HEIGHT_PX }} aria-hidden />
          ))}
          {items.map((item) => {
            const selected = item === value
            return (
              <div
                key={item}
                className={`flex shrink-0 items-center justify-center font-body text-xl leading-none transition-[color,font-weight] ${
                  selected
                    ? 'font-semibold text-text-primary-900'
                    : 'font-regular text-text-tertiary-600'
                }`}
                style={{ height: WHEEL_ITEM_HEIGHT_PX, scrollSnapAlign: 'center' }}
              >
                {item}
              </div>
            )
          })}
          {Array.from({ length: WHEEL_PAD_ROWS }, (_, i) => (
            <div key={`pad-bottom-${i}`} className="shrink-0" style={{ height: WHEEL_ITEM_HEIGHT_PX }} aria-hidden />
          ))}
        </div>
      </div>
    </div>
  )
}

/**
 * Mobile-only quarter-hour time picker: tap to open a bottom sheet with scroll wheels (hour + minute).
 */
export default function IncidentQuarterHourWheelPicker({
  value,
  onChange,
  triggerId,
  hourLabelId,
  minuteLabelId,
  describedBy,
  invalid = false,
}: IncidentQuarterHourWheelPickerProps) {
  const sheetTitleId = useId()
  const [open, setOpen] = useState(false)
  const [draftHour, setDraftHour] = useState('12')
  const [draftMinute, setDraftMinute] = useState<(typeof INCIDENT_QUARTER_MINUTES)[number]>('00')
  const previousFocusRef = useRef<HTMLElement | null>(null)

  const openSheet = useCallback(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const parts = splitHm24(value)
    setDraftHour(parts.hour !== '' ? padHourToken(parts.hour) || '12' : '12')
    const mm = parts.minute
    setDraftMinute(
      (INCIDENT_QUARTER_MINUTES as readonly string[]).includes(mm)
        ? (mm as (typeof INCIDENT_QUARTER_MINUTES)[number])
        : '00'
    )
    setOpen(true)
  }, [value])

  const closeSheet = useCallback(() => {
    setOpen(false)
    previousFocusRef.current?.focus()
  }, [])

  const applyAndClose = useCallback(() => {
    const next = combineHm24(draftHour, draftMinute)
    if (next) onChange(next)
    closeSheet()
  }, [closeSheet, draftHour, draftMinute, onChange])

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        closeSheet()
      }
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [closeSheet, open])

  const triggerLabel = formatIncidentTimeTriggerLabel(value)
  const displayLabel = triggerLabel || 'Select time'

  const sheet = open ? (
    <div
      className="fixed inset-0 z-50 flex flex-col justify-end"
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeSheet()
      }}
    >
      <div className="absolute inset-0 bg-background-overlay/60 backdrop-blur-sm" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={sheetTitleId}
        className="relative flex max-h-[min(420px,85dvh)] w-full flex-col rounded-t-2xl bg-background-primary shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border-secondary px-[16px] py-[12px]">
          <p id={sheetTitleId} className="m-0 font-display text-md font-semibold text-text-primary-900">
            Select time
          </p>
          <button
            type="button"
            className="rounded-md px-[12px] py-[8px] font-body text-sm font-semibold text-foreground-brand-solid"
            onClick={closeSheet}
          >
            Cancel
          </button>
        </div>
        <p className="m-0 px-[16px] pt-[8px] text-center font-body text-xs text-text-tertiary-600">
          24-hour · 15 minute steps
        </p>
        <div className="flex min-h-0 flex-1 flex-row gap-[8px] px-[16px] pb-[8px] pt-[4px]">
          <WheelColumn
            label="Hour"
            labelId={hourLabelId}
            items={INCIDENT_HOURS_24}
            value={draftHour}
            onValueChange={(h) => setDraftHour(h)}
          />
          <div className="flex shrink-0 items-center self-center pt-[28px] font-body text-2xl leading-none text-text-tertiary-600">
            :
          </div>
          <WheelColumn
            label="Min"
            labelId={minuteLabelId}
            items={INCIDENT_QUARTER_MINUTES}
            value={draftMinute}
            onValueChange={(m) =>
              setDraftMinute(m as (typeof INCIDENT_QUARTER_MINUTES)[number])
            }
          />
        </div>
        <div className="border-t border-border-secondary px-[16px] py-[16px]">
          <PrimaryButton type="button" fullWidth onClick={applyAndClose}>
            Done
          </PrimaryButton>
        </div>
      </div>
    </div>
  ) : null

  return (
    <>
      <button
        id={triggerId}
        type="button"
        onClick={openSheet}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        className={`${CLAIM_FORM_SELECT_CLASS} flex w-full min-w-0 items-center justify-between text-left ${
          invalid ? 'ring-2 ring-red-600' : ''
        }`.trim()}
      >
        <span className={triggerLabel ? 'text-text-primary-900' : 'text-text-placeholder'}>
          {displayLabel}
        </span>
        <span className="pointer-events-none ml-[8px] shrink-0 text-text-tertiary-600" aria-hidden>
          ▼
        </span>
      </button>
      {sheet ? createPortal(sheet, document.body) : null}
    </>
  )
}
